import { CustomProjection } from "@visx/geo";
import * as topojson from "topojson-client";
import stateTopology from "../us-states-topo.json";
import { geoCentroid } from "@visx/vendor/d3-geo";
import stateAbbrs from "../us-abbr.json";
import React from "react";
import { statesBackgroundColor } from "../map-container";

interface StateFeatureShape {
  type: "Feature";
  id: string;
  geometry: { coordinates: [number, number][][]; type: "Polygon" };
  properties: { name: string };
}

const { features: unitedStates } = topojson.feature(
  stateTopology as any,
  stateTopology.objects.states as any
) as unknown as {
  type: "FeatureCollection";
  features: StateFeatureShape[];
};

const coordOffsets: Record<string, number[]> = {
  FL: [11, 3],
  AK: [0, -4],
  CA: [-7, 0],
  NY: [5, 0],
  MI: [13, 20],
  LA: [-10, -3],
  HI: [-10, 10],
  ID: [0, 10],
  WV: [-2, 4],
  KY: [10, 0],
  TN: [0, 4],
};

/**
 * These states are too small to have text labels
 * inside of them and are usually displayed with pointers.
 * For simplicity they are omitted from this demo.
 */
const ignoredStates = [
  "VT",
  "NH",
  "MA",
  "RI",
  "CT",
  "NJ",
  "DE",
  "MD",
  "PI",
  "VI",
  "PR",
  "MP",
  "GU",
  "AS",
];

const MemoState = React.memo(({ feature, projection, path, width, i }: any) => {
  const coords: [number, number] | null = projection(geoCentroid(feature));
  const abbr: string = (stateAbbrs as any)[feature.id];

  if (abbr === "AK" || abbr === "HI") return null;

  if (coordOffsets[abbr] && coords) {
    coords[0] += coordOffsets[abbr][0];
    coords[1] += coordOffsets[abbr][1];
  }

  const stylesObj = {
    fill: "#FFF",
    fontFamily: "sans-serif",
    cursor: "default",
  };

  if (abbr === "HI") {
    stylesObj.fill = "#3C019C";
  }

  if (ignoredStates.includes(abbr)) {
    return (
      <path
        key={`map-feature-${i}`}
        d={path || ""}
        fill={statesBackgroundColor}
        stroke={"#fff"}
        strokeWidth={0.5}
      />
    );
  }

  return (
    <React.Fragment key={`map-feature-${i}`}>
      <path
        key={`map-feature-${i}`}
        d={path || ""}
        fill={statesBackgroundColor}
        stroke={"#fff"}
        strokeWidth={0.5}
      />
      {/* {displayLabels && ( */}
      <text
        transform={`translate(${coords})`}
        fontSize={Math.max(width / 75, 9)}
        style={stylesObj}
        textAnchor="middle"
      >
        {abbr}
      </text>
      {/* )} */}
    </React.Fragment>
  );
});

export const StateMap = ({ zoom, width }: { zoom: any; width: number }) => {
  return (
    <CustomProjection<StateFeatureShape>
      data={unitedStates}
      translate={[
        zoom.transformMatrix.translateX,
        zoom.transformMatrix.translateY,
      ]}
      scale={zoom.transformMatrix.scaleX}
    >
      {(customProjection) => (
        <>
          {customProjection.features.map(({ feature, path, projection }, i) => (
            <MemoState
              key={`map-feature-${i}`}
              feature={feature}
              path={path}
              projection={projection}
              width={width}
              i={i}
            />
          ))}
        </>
      )}
    </CustomProjection>
  );
};
